import type { AssetRef } from "@kss/schema";
import type { LoroMap, TreeID } from "loro-crdt";
import { Upload } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import type { UploadRecord } from "../../offline/uploadQueue";
import type { DocHandle } from "../../sync/docManager";
import { prepareAsset, queueAssetUpload } from "../../uploads/upload";
import { LoroTextInput } from "../textField";
import { useT } from "../../i18n";

/** Shared chrome for image/audio/video blocks: drop zone, upload status, caption. */
export function MediaShell({ handle, treeId, body, accept, readOnly, asset, upload, children, extraFields }: { handle: DocHandle; treeId: TreeID; body: LoroMap; accept: string; readOnly: boolean; asset: AssetRef | null; upload: (UploadRecord & { progress?: number }) | null; children: ReactNode; extraFields?: ReactNode }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const t = useT();
  const [hashing, setHashing] = useState<number | null>(null);
  const [drag, setDrag] = useState(false);

  const pick = async (file: File) => {
    setHashing(0);
    try {
      const ref = await prepareAsset(file, setHashing);
      // spec §8.10 step 2: the block references the asset before the upload completes (LWW value)
      body.set("asset", ref);
      handle.doc.commit();
      await queueAssetUpload(ref, handle.crdtDocId, treeId);
    } finally {
      setHashing(null);
    }
  };

  return (
    <div
      className={"rounded-md border bg-[var(--surface)] p-2.5 shadow-[var(--shadow-sm)] " + (drag ? "border-accent shadow-[0_0_0_3px_var(--color-accent-tint)]" : "border-neutral-200 dark:border-neutral-800")}
      onDragOver={(e) => { if (!readOnly) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { if (readOnly) return; e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) void pick(f); }}
      data-testid="media-block"
      data-fill-column
    >
      {asset ? <div data-fill-surface data-fill-center>{children}</div> : (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Upload size={16} />
          {readOnly ? t("media.none") : t("media.drop")}
          {!readOnly && <button className="underline" onClick={() => fileRef.current?.click()}>{t("media.choose")}</button>}
        </div>
      )}
      {!readOnly && <input ref={fileRef} type="file" accept={accept} className="hidden" data-testid="media-file-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ""; }} />}
      {hashing !== null && <div className="text-xs text-neutral-500">{t("media.hashing", { percent: Math.round(hashing * 100) })}</div>}
      {upload && upload.state !== "done" && (
        <div className="text-xs text-neutral-500" data-testid="upload-state" data-state={upload.state}>
          {upload.state === "failed"
            ? t("media.uploadFailed", { error: upload.error ?? "" })
            : t(upload.state === "queued" ? "media.uploadState.queued" : "media.uploadState.uploading", { percent: Math.round((upload.progress ?? 0) * 100) })}
        </div>
      )}
      {asset && !readOnly && <button className="mt-1 text-xs text-neutral-500 underline" onClick={() => fileRef.current?.click()}>{t("media.replace")}</button>}
      {extraFields}
      <LoroTextInput handle={handle} map={body} field="caption" placeholder={t("media.caption")} readOnly={readOnly} testId="media-caption" />
    </div>
  );
}
